// 2026-05-26 - FormuLearning - Step 1 RLD 목록 섹션
// 2026-11-03 수정 - 이슈 #114: viewer 는 상세 조회·허가 품목 불러오기·건수 조회·대조약 선택 안 함(비활성 + «조회 전용 계정»).
// 파일 위치: frontend/src/features/step1/RldSection.tsx
// 2026-06-28 - 다나와식 대조약 비교(체크 선택 → 매트릭스 모달) 추가 (이슈 #25)
// 2026-09-17 - 이슈 #79(M915-03): **검색 중 상세 보강·선택 잠금 + 엇갈린 보강 응답 폐기.**
//   상세 보강 API 는 목록 **index** 로 **저장본** 항목을 보강해 돌려준다. 검색 도중(rld 패널 수신 ~ 저장 전)
//   화면 목록은 새 검색 것인데 저장본은 직전 검색 것이라, 새 목록 i 번 자리에 **직전 검색의 i 번 제품**이
//   덮였다(260917 실측: valsartan 목록 0·1번에 코다론주사·코다론정, 2번은 404). 검색 중에는 부르지 않고,
//   응답이 와도 그 자리의 `item_seq` 가 다르거나 목록이 바뀌었으면 버린다.
// 2026-09-23 - 이슈 #86: **`item_seq` 로 보강 — 검색 중에도 보강한다.** 서버가 저장본·진행 중 검색 목록에서 품목을 찾으므로
//   #79 의 검색 중 보강 정지를 푼다(선택 잠금은 유지). 백그라운드는 3건씩 동시. 허가상세 실패 응답(`enriched=false`)은
//   오류로 표시해 클릭으로 재시도한다.
// 2026-09-23 - 이슈 #73: **허가 품목 목록 · 지연 조회 · 페이지 나눔.** 대조약만 있는 목록이면 허가 품목 건수를 조회해
//   안내하고(500건 이상은 확인 후) 병합 수집한다(U1). 백그라운드 3건 보강을 없애고 펼침·선택 때만 상세를 조회한다(U2 —
//   목록이 수백 건이 되어 전량 조회는 시간·호출량이 크다). 20건씩 나눠 그리고, 대조약 행에 배지를 단다.
//   검토 `ai-agent/claude/260923_#73_ApprovedDrug전환검토/검토_260923_ApprovedDrug전환.md` §3.
// 2026-09-23 - 이슈 #73 P3(M901-08): **2차 필터**(`RldFilterBar`·`rldFilter`). 페이지 나눔은 필터 결과의 원래 위치 번호 위에서
//   한다. 총중량 필터를 켜면 다른 필터로 좁혀진 미조회 품목만 3건씩 상세 조회한다(U2).
// 2026-09-23 - 이슈 #73 P4(M915-05): 섹션 제목 «Reference Drug» → **«Approved Drug»**(`Q-260923-01` 잠정 — Step 1 목록만.
//   선택 버튼 «Select as Reference»·Step 3 문구는 선택 품목의 역할이라 유지). 선택 품목이 대조약이 아니거나 총중량을
//   산출할 수 없으면(바이오·액상) 안내 한 줄(U3, `Q-260923-02` 권고).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Collecting from './Collecting'
import RldFilterBar from './RldFilterBar'
import { applyFilter, defaultFilter, ingredientChips, passesBase, permitStatusLabel, weightBand, type RldFilter } from './rldFilter'
import { openRldCompareWindow } from './rldCompare'
import { recordOperation } from '../../api/operations'
import { collectRld, fetchRldCount, fetchRldDetail, type RldCount, type Step1Data } from '../../api/step1'
import { useCanWrite, READ_ONLY_TITLE } from '../../hooks/useCanWrite'

// 비교 모드에서 동시에 비교 가능한 최대 대조약 수(매트릭스 가독성 한계).
const MAX_COMPARE = 5
// 한 페이지에 그리는 품목 수(#73) — 허가 품목까지 모으면 수백 건이다(valsartan 541건).
const PAGE_SIZE = 20
// 병합 수집으로 허가 목록에서 온 행의 출처 표시(backend `rld_merge.SOURCE_PERMIT`).
const SOURCE_PERMIT = '허가목록'
// 총중량 필터용 상세 조회 동시 건수 · 확인창 기준(품목당 외부 3건 — 일일 10,000건, `Q-260923-05`).
const WEIGHT_FETCH_CONCURRENCY = 3
const WEIGHT_FETCH_CONFIRM = 100

interface Props {
  rldList: Record<string, unknown>[]
  selectedRldId: number | null
  onSelect: (idx: number) => void
  loading?: boolean
  projectId?: string
  /** 검색 진행 중(#79) — 대조약 선택을 멈춘다(상세 보강은 item_seq 기준이라 계속한다, #86) */
  locked?: boolean
  /** 허가 품목 병합 수집 후 저장된 Step 1 으로 화면을 바꾼다(#73) */
  onListReplaced?: (data: Step1Data) => void
}

// 상세 패널에 표시할 RLD 항목(키, 라벨, 장문 여부). 값이 있는 항목만 노출.
// 회의 피드백(260628) — 항목·순서를 다음 11종으로 확정.
// long=true는 문단형(효능·용법용량)으로 라벨 위에 본문을 펼쳐 표시한다.
// always=true 항목은 값이 없어도 '—'로 항상 노출(제형설계에 필수인 수치 — 요청 260629).
const FIELDS: { key: string; label: string; long?: boolean; always?: boolean }[] = [
  { key: 'product_name', label: 'Product Name' },
  { key: 'maker', label: 'Manufacturer' },
  { key: 'main_ingredient_detail', label: 'Active Ingredient (Content)', always: true },
  { key: 'appearance', label: 'Appearance' },
  { key: 'weight_mg', label: 'Total Weight', always: true },
  { key: 'insurance_price', label: 'Insurance Drug Price' },
  { key: 'packaging', label: 'Packing Type' },
  { key: 'excipients', label: 'Excipients' },
  { key: 'storage', label: 'Storage Conditions' },
  { key: 'dosage_usage', label: 'Dosage / Administration', long: true },
  { key: 'efficacy', label: 'Efficacy', long: true },
  { key: 'shelf_life', label: 'Shelf Life' },
]

function fmt(v: unknown): string | null {
  if (v === null || v === undefined || v === '') return null
  return String(v)
}

// 항목값 표시 문자열. Weight는 mg 단위를 덧붙인다.
function fieldValue(rld: Record<string, unknown>, key: string): string | null {
  const v = fmt(rld[key])
  if (!v) return null
  if (key === 'weight_mg') return `${v} mg`
  return v
}

export default function RldSection({ rldList, selectedRldId, onSelect, loading, projectId, locked, onListReplaced }: Props) {
  // viewer(#114) — 목록 보기·필터·비교·펼침은 허용. 상세 조회(외부 조회+저장)·허가 품목 수집·건수 조회·대조약 선택은 하지 않는다.
  const canWrite = useCanWrite()
  // 비교 모드 상태(UI 전용 — 저장하지 않음). compareMode: 체크박스 노출, checked: 비교 대상, showMatrix: 매트릭스 모달.
  const [compareMode, setCompareMode] = useState(false)
  const [checked, setChecked] = useState<number[]>([])
  // 상세 펼침 상태(선택과 분리) — 행 클릭은 '상세 보기', 선택은 'Select as Reference' 버튼으로 명시화.
  const [expandedId, setExpandedId] = useState<number | null>(null)

  // 지연 보강(요청 260629) — 검색은 목록만 받고, 상세는 펼침·선택 때 조회한다(#73 U2 — 백그라운드 보강 없음).
  // overlay: index→보강된 RLD, loadingState: index→조회중 표시. ref는 중복호출 방지용.
  const [overlay, setOverlay] = useState<Record<number, Record<string, unknown>>>({})
  const [loadingState, setLoadingState] = useState<Record<number, boolean>>({})
  const [errored, setErrored] = useState<Record<number, boolean>>({})
  const inflight = useRef<Set<number>>(new Set())
  const done = useRef<Set<number>>(new Set())
  // 페이지(#73)와 허가 품목 안내·수집 상태(#73 U1).
  const [page, setPage] = useState(0)
  // 건수·오류는 조회한 목록 시그니처와 함께 쥔다 — 목록이 바뀌면 지난 안내가 저절로 사라진다.
  const [permitCount, setPermitCount] = useState<{ sig: string; count: RldCount } | null>(null)
  const [collecting, setCollecting] = useState(false)
  const [collectError, setCollectError] = useState<{ sig: string; message: string } | null>(null)
  // 2차 필터(#73 P3) — 화면 전용, 목록이 바뀌면 초기화.
  const [filter, setFilter] = useState<RldFilter>(defaultFilter)

  const getRld = useCallback((i: number) => overlay[i] ?? rldList[i], [overlay, rldList])

  // 단건 상세 보강. 선택(force=false — 이미 조회·실패한 품목은 건너뜀)과 펼침·재시도(force=true)에서 호출.
  // force=true는 이전 실패/완료 표시를 무시하고 다시 시도(사용자 명시적 클릭).
  const ensureDetail = useCallback(
    async (i: number, force = false) => {
      if (!projectId || !canWrite) return
      if (inflight.current.has(i)) return
      if (!force && done.current.has(i)) return
      if (!force && (overlay[i] ?? rldList[i])?.enriched) {
        done.current.add(i)
        return
      }
      // 목록이 바뀌면(item_seq 시그니처 → 초기화 effect) 집합이 새로 만들어진다.
      // 요청 시점의 집합을 쥐어 두고, 응답이 왔을 때 집합이 바뀌었으면 지난 목록의 응답이다(#79).
      const inflightSet = inflight.current
      const doneSet = done.current
      const seq = rldList[i]?.item_seq
      const stale = () => doneSet !== done.current
      inflightSet.add(i)
      setErrored((s) => { const n = { ...s }; delete n[i]; return n })
      setLoadingState((s) => ({ ...s, [i]: true }))
      try {
        const rld = await fetchRldDetail(projectId, i, seq as string | undefined)
        if (stale()) return
        if (rld.item_seq !== seq) return // 다른 제품이 왔다 — 덮지 않는다(재시도는 클릭으로)
        doneSet.add(i)
        if (!rld.enriched) {
          setErrored((s) => ({ ...s, [i]: true })) // 허가상세 조회 실패 — 서버는 완료로 남기지 않았다(#86)
          return
        }
        setOverlay((o) => ({ ...o, [i]: rld }))
      } catch {
        if (stale()) return
        doneSet.add(i) // 선택 시 반복 요청 방지(클릭 force로 재요청 가능)
        setErrored((s) => ({ ...s, [i]: true }))
      } finally {
        inflightSet.delete(i)
        if (!stale()) {
          setLoadingState((s) => {
            const n = { ...s }
            delete n[i]
            return n
          })
        }
      }
    },
    [projectId, overlay, rldList, canWrite],
  )

  // 새 검색으로 대조약 목록이 바뀌면 비교 선택·펼침·보강 상태를 초기화.
  // item_seq 시그니처 기준 — 무관한 SSE 갱신에는 반응하지 않음.
  const rldSignature = rldList.map((r) => r.item_seq ?? '').join('|')
  useEffect(() => {
    setCompareMode(false)
    setChecked([])
    setExpandedId(null)
    setOverlay({})
    setLoadingState({})
    setErrored({})
    inflight.current = new Set()
    done.current = new Set()
    const initial = defaultFilter()
    setFilter(initial)
    // 선택한 품목이 있는 페이지부터 보인다(목록이 바뀌어도 선택은 같은 품목의 새 위치 — #73).
    // 2026-11-03 (#105, M1002-20): 기본 필터가 취소·만료를 빼므로 원래 번호가 아니라 **기본 필터 통과 목록 안의 위치**로 센다.
    //   선택 품목이 기본 필터에 가려지면(취소·만료 품목을 고른 경우) 첫 페이지 — «선택한 품목이 필터에 가려져 있습니다» 가 알린다.
    const pos = selectedRldId != null ? applyFilter(rldList, initial).indexOf(selectedRldId) : -1
    setPage(pos >= 0 ? Math.floor(pos / PAGE_SIZE) : 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 목록이 바뀔 때만 초기화한다(선택 변경은 페이지를 옮기지 않는다)
  }, [rldSignature])

  // 허가 품목 안내(#73 U1) — 목록에 허가 목록 출처 행이 없으면(대조약만 · 옛 프로젝트 · 500건 이상 보류) 건수를 조회한다.
  // 건수 조회는 외부 2건(각 1행)이다. 검색 중·수집 중에는 부르지 않고, 목록 시그니처마다 한 번만 부른다.
  const hasPermitRows = rldList.some((r) => Array.isArray(r.sources) && (r.sources as string[]).includes(SOURCE_PERMIT))
  useEffect(() => {
    if (!projectId || locked || loading || hasPermitRows || !canWrite) return
    let cancelled = false
    fetchRldCount(projectId)
      .then((c) => { if (!cancelled) setPermitCount({ sig: rldSignature, count: c }) })
      .catch(() => { /* 안내만 생략 — 목록 자체는 그대로 쓸 수 있다 */ })
    return () => {
      cancelled = true
    }
  }, [rldSignature, projectId, locked, loading, hasPermitRows, canWrite])

  const count = !hasPermitRows && !locked && permitCount?.sig === rldSignature ? permitCount.count : null
  const collectMessage = collectError?.sig === rldSignature ? collectError.message : null

  async function handleCollect() {
    if (!projectId || !count?.permit_total) return
    const total = count.permit_total
    if (total >= count.threshold && !window.confirm(
      `허가 품목 ${total.toLocaleString()}건(취소·만료 포함, 예상)을 불러옵니다.\n` +
      `취소·만료 품목도 포함되어 목록이 길어집니다. 계속할까요?`)) return
    setCollecting(true)
    setCollectError(null)
    try {
      const updated = await collectRld(projectId)
      onListReplaced?.(updated)
      setPermitCount(null) // 취소 품목뿐이라 목록이 그대로여도 다시 안내하지 않는다
    } catch (e) {
      const status = (e as { response?: { status?: number } }).response?.status
      setCollectError({ sig: rldSignature, message: status === 409 ? '검색이 진행 중입니다. 검색이 끝난 뒤 다시 시도하세요.'
        : '허가 품목을 불러오지 못했습니다. 잠시 후 다시 시도하세요.' })
    } finally {
      setCollecting(false)
    }
  }

  // 2차 필터(#73 P3). 판정은 조회된 상세(overlay)까지 합친 행으로 한다 — 총중량은 상세 조회분이다.
  const rows = useMemo(() => rldList.map((r, i) => overlay[i] ?? r), [rldList, overlay])
  const visible = useMemo(() => applyFilter(rows, filter), [rows, filter])
  const chips = useMemo(() => ingredientChips(rldList), [rldList])
  const pageCount = Math.max(1, Math.ceil(visible.length / PAGE_SIZE))
  const pageNow = Math.min(page, pageCount - 1)
  const selectedHidden = selectedRldId != null && selectedRldId < rldList.length && !visible.includes(selectedRldId)

  // 총중량 필터 — 다른 필터를 통과했는데 총중량을 모르는(미조회) 품목. 조회 실패한 품목은 다시 부르지 않는다(펼침으로 재시도).
  const weightTargets = useCallback(
    (f: RldFilter) => rows.flatMap((row, i) => (passesBase(row, f) && weightBand(row) === 'unknown' && !errored[i] ? [i] : [])),
    [rows, errored],
  )
  const baseKey = JSON.stringify({ ...filter, weightBands: [] })
  const weightProgress = useMemo(() => {
    if (!filter.weightOn) return null
    const base = rows.filter((row) => passesBase(row, filter))
    return { done: base.filter((row) => weightBand(row) !== 'unknown').length, total: base.length }
  }, [rows, filter])
  useEffect(() => {
    if (!filter.weightOn || !projectId) return
    const targets = weightTargets(filter)
    if (targets.length === 0) return
    let cancelled = false
    let next = 0
    const worker = async () => {
      while (!cancelled && next < targets.length) {
        await ensureDetail(targets[next++]) // 대상은 시작 시점의 미조회 품목 — 조회를 마친 품목은 ensureDetail 이 건너뛴다
      }
    }
    for (let w = 0; w < WEIGHT_FETCH_CONCURRENCY; w++) void worker()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 필터(총중량 구간 제외)·목록이 바뀔 때만 조회 대상을 다시 고른다
  }, [baseKey, rldSignature, projectId])

  function changeFilter(next: RldFilter) {
    if (next.weightOn && !filter.weightOn && canWrite) { // viewer 는 상세를 조회하지 않으므로 묻지 않는다(#114)
      const n = weightTargets(next).length
      if (n > WEIGHT_FETCH_CONFIRM && !window.confirm(
        `총중량을 알려면 품목 ${n.toLocaleString()}건의 상세를 조회해야 합니다.\n` +
        `다른 필터로 먼저 좁히면 조회가 줄어듭니다. 계속할까요?`)) return
    }
    setFilter(next)
    setPage(0)
  }

  function toggleCompareMode() {
    setCompareMode((on) => !on)
    setChecked([])
  }

  function toggleChecked(idx: number) {
    setChecked((prev) => {
      if (prev.includes(idx)) return prev.filter((i) => i !== idx)
      if (prev.length >= MAX_COMPARE) return prev
      return [...prev, idx]
    })
  }

  // 카드 펼침 토글 + 미보강 항목은 우선 보강.
  function toggleExpand(i: number) {
    if (expandedId === i) {
      setExpandedId(null)
      return
    }
    setExpandedId(i)
    if (!getRld(i)?.enriched) ensureDetail(i, true) // 클릭은 명시적 — 실패/완료 무시하고 재시도
  }

  return (
    <div className="mb-4">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-text-secondary">Approved Drug</h4>
        {rldList.length >= 2 && (
          <button
            onClick={toggleCompareMode}
            className="rounded px-2 py-0.5 text-[10px] font-medium transition-colors"
            style={{
              borderWidth: 1,
              borderColor: compareMode ? 'var(--border-purple)' : 'var(--border-subtle)',
              color: compareMode ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
              backgroundColor: compareMode ? 'var(--bg-row-active)' : 'transparent',
            }}
          >
            {compareMode ? 'Cancel' : 'Compare'}
          </button>
        )}
      </div>

      {compareMode && (
        <p className="mb-2 text-[10px] text-text-muted">Select approved drugs to compare (up to {MAX_COMPARE}).</p>
      )}

      {!!count?.permit_total && (
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2 text-[11px]"
             style={{ borderColor: 'var(--border-subtle)' }}>
          <span className="text-text-secondary">
            허가 품목 {count.permit_total.toLocaleString()}건(취소·만료 포함)이 더 있습니다
            {rldList.length > 0 ? ` — 지금은 대조약 ${rldList.length}건만 표시 중` : ''}
          </span>
          <button
            onClick={handleCollect}
            disabled={collecting || locked || !canWrite}
            title={canWrite ? undefined : READ_ONLY_TITLE}
            className={`rounded px-2.5 py-1 text-[10px] font-semibold${collecting || locked || !canWrite ? ' opacity-50' : ''}`}
            style={{ borderWidth: 1, borderColor: 'var(--border-purple)', color: 'var(--color-text-primary)' }}
          >
            {collecting ? '불러오는 중…' : '허가 품목 불러오기'}
          </button>
          {collectMessage && <p className="w-full text-[10px]" style={{ color: 'var(--color-danger)' }}>{collectMessage}</p>}
        </div>
      )}

      {rldList.length > PAGE_SIZE && (
        <RldFilterBar
          filter={filter}
          onChange={changeFilter}
          onReset={() => { setFilter(defaultFilter()); setPage(0) }}
          ingredients={chips}
          shown={visible.length}
          total={rldList.length}
          weightProgress={weightProgress}
          hasReference={hasPermitRows && rldList.some((r) => r.is_reference === true)}
        />
      )}

      {selectedHidden && (
        <p className="mb-2 text-[10px] text-text-muted">선택한 품목이 필터에 가려져 있습니다.</p>
      )}

      {visible.length > PAGE_SIZE && (
        <div className="mb-2 flex items-center justify-end gap-2 text-[10px] text-text-secondary">
          <button onClick={() => setPage(Math.max(0, pageNow - 1))} disabled={pageNow === 0}
                  className={`px-1.5${pageNow === 0 ? ' opacity-40' : ''}`}>‹</button>
          <span>
            {pageNow * PAGE_SIZE + 1}–{Math.min((pageNow + 1) * PAGE_SIZE, visible.length)} / {visible.length.toLocaleString()}
          </span>
          <button onClick={() => setPage(Math.min(pageCount - 1, pageNow + 1))}
                  disabled={pageNow + 1 >= pageCount}
                  className={`px-1.5${pageNow + 1 >= pageCount ? ' opacity-40' : ''}`}>›</button>
        </div>
      )}

      {rldList.length === 0 ? (
        loading ? <Collecting /> : <p className="text-[11px] text-text-muted">표시할 허가 품목 정보가 없습니다.</p>
      ) : (
        <div className="panel-in space-y-2">
          {visible.length === 0 && <p className="text-[11px] text-text-muted">필터에 맞는 품목이 없습니다.</p>}
          {visible.slice(pageNow * PAGE_SIZE, (pageNow + 1) * PAGE_SIZE).map((i) => {
            const rld = getRld(i)
            const selected = selectedRldId === i
            const isChecked = checked.includes(i)
            const expanded = expandedId === i
            const enriched = !!rld?.enriched
            const isLoading = !!loadingState[i]
            return (
              <div
                key={i}
                className="flex items-start rounded-md border transition-all"
                style={
                  selected
                    ? {
                        borderColor: 'var(--color-accent-purple)',
                        boxShadow: '0 0 0 1px var(--color-accent-purple)',
                        backgroundColor: 'var(--bg-row-active)',
                      }
                    : { borderColor: 'var(--border-subtle)', backgroundColor: 'transparent' }
                }
              >
                {compareMode && (
                  <label className="flex cursor-pointer items-center px-2 pt-3.5">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      disabled={!isChecked && checked.length >= MAX_COMPARE}
                      onChange={() => toggleChecked(i)}
                      className="h-3.5 w-3.5 cursor-pointer accent-[var(--color-accent-purple)]"
                    />
                  </label>
                )}
                <div className="min-w-0 flex-1">
                <button onClick={() => toggleExpand(i)} className="w-full p-3 pb-1 text-left">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-text-primary">
                      {hasPermitRows && rld.is_reference === true && (
                        <span className="mr-1.5 rounded px-1 py-px text-[9px] font-semibold"
                              style={{ borderWidth: 1, borderColor: 'var(--border-purple)', color: 'var(--color-text-secondary)' }}>
                          대조약
                        </span>
                      )}
                      {fmt(rld.product_name)}
                      {permitStatusLabel(rld) && (
                        <span className="ml-1.5 rounded px-1 py-px text-[9px]"
                              style={{ borderWidth: 1, borderColor: 'var(--color-warn)', color: 'var(--color-warn)' }}>
                          {permitStatusLabel(rld)}
                        </span>
                      )}
                    </span>
                    <span className="shrink-0 text-[10px] text-text-secondary">{fmt(rld.maker)}</span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-[10px] text-text-secondary">
                    {fmt(rld.main_ingredient) && <span>API: {fmt(rld.main_ingredient)}</span>}
                    {fmt(rld.weight_mg) && <span>Weight: {fmt(rld.weight_mg)} mg</span>}
                  </div>
                  {/* 상태머신: 미조회 → 조회중 → 보기 / 실패(클릭 재시도) */}
                  <p className="mt-1 text-[9px] text-text-muted">
                    {expanded
                      ? '▲ Hide details'
                      : isLoading
                        ? '⏳ Loading detail…'
                        : enriched
                          ? '▼ View details'
                          : errored[i]
                            ? '⚠ Load failed — click to retry'
                            : '▼ Load details'}
                  </p>
                </button>

                <div className="flex items-center gap-2 px-3 pb-2">
                  <button
                    onClick={() => { onSelect(i); ensureDetail(i) }}
                    disabled={selected || locked || !canWrite}
                    title={canWrite || selected ? undefined : READ_ONLY_TITLE}
                    className={`rounded px-2.5 py-1 text-[10px] font-semibold transition-colors${(locked || !canWrite) && !selected ? ' opacity-50' : ''}`}
                    style={
                      selected
                        ? { backgroundColor: 'var(--color-accent-purple)', color: '#fff', cursor: 'default' }
                        : {
                            borderWidth: 1,
                            borderColor: 'var(--border-purple)',
                            color: 'var(--color-text-secondary)',
                            backgroundColor: 'transparent',
                          }
                    }
                  >
                    {selected ? '✓ Selected as Reference' : 'Select as Reference'}
                  </button>
                  {selected && <span className="text-[9px] text-text-muted">used in Step 3 (total weight)</span>}
                </div>
                {/* 선택 품목 안내(#73 U3 · Q-260923-02 권고 — 막지 않고 알린다) */}
                {selected && rld.is_reference === false && (
                  <p className="px-3 pb-1 text-[10px]" style={{ color: 'var(--color-warn)' }}>
                    식약처 지정 대조약이 아닙니다 — 생동성 기준으로 쓰려면 확인이 필요합니다.
                  </p>
                )}
                {selected && enriched && weightBand(rld) === 'none' && (
                  <p className="px-3 pb-2 text-[10px]" style={{ color: 'var(--color-warn)' }}>
                    총중량을 산출할 수 없는 품목입니다(부피·단위 표기) — Step 3 에는 첨가제만 넘어갑니다.
                  </p>
                )}

                {expanded && (
                  <div className="border-t px-3 py-2" style={{ borderColor: 'var(--border-subtle)' }}>
                    {!enriched ? (
                      isLoading ? (
                        <p className="text-[11px] text-text-muted">상세 정보를 불러오는 중…</p>
                      ) : (
                        <button
                          onClick={() => ensureDetail(i, true)}
                          disabled={!canWrite}
                          title={canWrite ? undefined : READ_ONLY_TITLE}
                          className="text-[11px] underline disabled:opacity-50"
                          style={{ color: 'var(--color-accent-teal)' }}
                        >
                          {errored[i] ? '⚠ 불러오기 실패 — 다시 시도' : '상세 정보 불러오기'}
                        </button>
                      )
                    ) : (
                    <>
                    {fmt(rld.image_url) && (
                      <img
                        src={fmt(rld.image_url) as string}
                        alt={fmt(rld.product_name) ?? 'Approved drug'}
                        loading="lazy"
                        className="mb-2 max-h-28 rounded border bg-white object-contain p-1"
                        style={{ borderColor: 'var(--border-subtle)' }}
                      />
                    )}
                    <div className="space-y-1.5">
                      {FIELDS.map(({ key, label, long, always }) => {
                        const v = fieldValue(rld, key)
                        if (!v && !always) return null
                        const display = v ?? '—'
                        if (long) {
                          return (
                            <div key={key}>
                              <p className="text-[10px] font-semibold uppercase tracking-wider text-text-secondary">{label}</p>
                              <p className="mt-0.5 text-[11px] leading-relaxed text-text-body">{display}</p>
                            </div>
                          )
                        }
                        return (
                          <div key={key} className="grid grid-cols-[auto_1fr] gap-x-3 text-[11px]">
                            <span className="whitespace-nowrap font-medium text-text-secondary">{label}</span>
                            <span className={v ? 'text-text-body' : 'text-text-muted'}>{display}</span>
                          </div>
                        )
                      })}
                      {FIELDS.every(({ key, always }) => always || !fieldValue(rld, key)) && (
                        <p className="text-[11px] text-text-muted">주성분·총중량 외 추가 상세 정보가 없습니다.</p>
                      )}
                    </div>
                    </>
                    )}
                  </div>
                )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {compareMode && checked.length >= 2 && (
        <button
          onClick={() => { recordOperation('step1_rld_compare', projectId); openRldCompareWindow(checked.map((i) => rldList[i])) }}
          className="mt-2 w-full rounded-md py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90"
          style={{ backgroundColor: 'var(--color-accent-purple)' }}
        >
          Compare {checked.length} selected
        </button>
      )}
    </div>
  )
}
